/**
 * Convert Musey-neutral lyricPayload into Folia LyricData (stage-side only).
 */
import type { LyricData } from '../../vendor/folia-src/types'
import { lyricDataFromLines, lyricDataFromPlainText } from './parseLrc'
import type { LyricPayload } from '../../app/protocol'

type WordLike = { text?: string; start?: number; end?: number }
type LineLike = { text?: string; start?: number; end?: number; words?: WordLike[] | null }


function buildWordsFromText(text: string, startTime: number, endTime: number) {
  const raw = String(text || '')
  const chars = Array.from(raw)
  if (!chars.length) {
    return [{ text: raw || ' ', startTime, endTime }]
  }
  const duration = Math.max(0.01, endTime - startTime)
  const step = duration / chars.length
  return chars.map((ch, i) => {
    const s = startTime + i * step
    return { text: ch, startTime: s, endTime: Math.min(endTime, s + step) }
  })
}

function mapSyncWords(
  syncWords: WordLike[] | null | undefined,
  fallbackText: string,
  startTime: number,
  endTime: number,
) {
  if (!Array.isArray(syncWords) || !syncWords.length) {
    return buildWordsFromText(fallbackText, startTime, endTime)
  }
  return syncWords
    .map((w) => ({
      text: String(w?.text ?? ''),
      startTime: Number(w?.start ?? startTime) || startTime,
      endTime: Number(w?.end ?? endTime) || endTime,
    }))
    .filter((w) => w.text)
}

function lineFromText(text: string, startTime: number, endTime: number) {
  return {
    words: buildWordsFromText(text, startTime, endTime),
    startTime,
    endTime,
    fullText: text,
  }
}

function buildIntroCreditTexts(meta: {
  title?: string
  artist?: string
  instrumental?: boolean
  lyricist?: string
  composer?: string
}) {
  const title = String(meta.title || '').trim()
  const author = String(meta.artist || '').trim()
  const lyricist = String(meta.lyricist || author).trim()
  const composer = String(meta.composer || author).trim()
  const texts: string[] = []
  if (title) texts.push(title)
  if (!meta.instrumental && lyricist) texts.push(`作词：${lyricist}`)
  if (composer) texts.push(`作曲：${composer}`)
  return texts
}

function buildIntroCreditLines(
  meta: {
    title?: string
    artist?: string
    instrumental?: boolean
    lyricist?: string
    composer?: string
  },
  firstLyricStart: number,
) {
  const texts = buildIntroCreditTexts(meta)
  if (!texts.length) return []

  const INTRO_WINDOW_MAX = 3
  const HOLD_FADE_BEFORE = 0.5
  const hasFirst = Number.isFinite(firstLyricStart)
  if (hasFirst && firstLyricStart < 0.6) return []

  const creditBound = hasFirst
    ? Math.min(firstLyricStart, INTRO_WINDOW_MAX)
    : INTRO_WINDOW_MAX
  const slot = creditBound / texts.length
  const lines = texts.map((text, i) => {
    const startTime = i * slot
    const endTime = Math.min(creditBound, (i + 1) * slot)
    return lineFromText(text, startTime, endTime)
  })

  const title = String(meta.title || '').trim()
  if (title && hasFirst) {
    const holdStart = creditBound
    const holdEnd = Math.max(holdStart, firstLyricStart - HOLD_FADE_BEFORE)
    if (holdEnd - holdStart >= 0.4) {
      lines.push(lineFromText(title, holdStart, holdEnd))
    }
  }

  return lines
}

function metaFrom(payload: LyricPayload) {
  return {
    title: payload.title,
    artist: payload.artist,
    instrumental: payload.instrumental,
    lyricist: payload.lyricist,
    composer: payload.composer,
  }
}

export function lyricPayloadToLyricData(payload: LyricPayload | null | undefined): LyricData {
  const meta = metaFrom(payload || { kind: 'empty' })
  const title = meta.title || undefined
  const artist = meta.artist || undefined

  if (payload?.kind === 'sync' && Array.isArray(payload.lines) && payload.lines.length) {
    const syncLines = payload.lines as LineLike[]
    const syncWords = Array.isArray(payload.words) ? payload.words : null
    const lines = syncLines.map((row, index) => {
      const text = String(row?.text ?? '').trim() || ' '
      const startTime = Number(row?.start) || 0
      const nextStart =
        index + 1 < syncLines.length
          ? Number(syncLines[index + 1]?.start)
          : Number.NaN
      const endTime = Number.isFinite(Number(row?.end))
        ? Number(row.end)
        : Number.isFinite(nextStart)
          ? nextStart
          : startTime + 4

      let wordsForLine: WordLike[] | null = null
      const nested = Array.isArray(row?.words) ? row.words : null
      if (nested?.length) {
        wordsForLine = nested
      } else if (syncWords?.length) {
        const owned = syncWords.filter((w) => {
          const ws = Number(w?.start)
          return Number.isFinite(ws) && ws >= startTime && ws < endTime
        })
        if (owned.length) wordsForLine = owned
      }

      return {
        words: mapSyncWords(wordsForLine, text, startTime, endTime),
        startTime,
        endTime,
        fullText: text,
      }
    })
    const intro = buildIntroCreditLines(meta, lines[0]!.startTime)
    return lyricDataFromLines([...intro, ...lines], {
      title,
      artist,
      isWordByWord: Boolean(syncWords?.length),
    })
  }

  if (payload?.kind === 'plain') {
    const plain = String(payload.text || '').trim()
    if (plain) return lyricDataFromPlainText(plain, { title, artist })
  }

  return lyricDataFromLines(buildIntroCreditLines(meta, Number.NaN), { title, artist })
}
